import React from 'react';
import { Box, LinearProgress, makeStyles, Tooltip } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import AssignmentIcon from '@material-ui/icons/Assignment';
import {
    FunctionField,
    List,
    Pagination,
    Record,
    TextField as RaTextField,
    useListContext,
    useTranslate
} from 'react-admin';
import DataGridCustom from '../UI/DataGrid';
import RightInfoDrawer from '../drawer/RightInfoDrawer';
import ActionList from './actions/';
import Filters from './filters/simpleListFilter1';
import { SOURCE_BOARDS } from '../../providers/resources';
import { ancs } from '../../pages/paths';
import { useDispatch, useSelector } from 'react-redux';
import { selectAnc } from '../../redux/actions/ancs';
import { SourceBoard } from '../../utils/types';
import { toFormatDate } from '../../utils/date';
import CreationDateField from './columns/CreationDateField';
import ShowLastNoteIcon1 from '../showLastNoteIcon/showLastNoteIcon1';
import { MAINTENANCE, SLUDGE_EMPTYING } from '../../utils/rikutec';
import { getNameWithoutPrefix } from '../../utils/resources/MetadataAvatarType';
import useAvatarAppLinks from './hooks/useAvatarAppLinks';
import { useRolePermissions } from '../../hooks/useRolePermissions';

export const hasRoles = (roles: string[] = [], permissions: string[] = []) => {
    return roles.every(
        (role) =>
            -1 !==
            permissions.findIndex((prole) => prole.toLowerCase() === role.toLowerCase()),
    );
};

export interface EnabledColumns {
    processingCapacity: boolean;
    sludgeStorageVolume: boolean;
    totalUsefulVolume: boolean;
    currentState: boolean;
    model: boolean;
}

export type AvatarActions = 'ADD_VP' | 'EDIT_VP' | 'ADD_WC' | 'EDIT_WC' | 'NONE';

const useStyles = makeStyles(() => ({
    checkbox: {},
    list: {},
}));

const ListPagination = (props: any) => <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />;

interface Props {
    enabledColumns: EnabledColumns;
    toggleColumn: (column: string, enabled: boolean) => void;
}

const listProps = {
    resource: SOURCE_BOARDS,
    hasCreate: false,
    hasEdit: false,
    hasList: true,
    hasShow: false,
    basePath: ancs,
    sort: { field: 'creationDate', order: 'DESC' },
    perPage: 50,
    filter: {
        deleted: false,
    },
};

const LoadingBar = () => {
    const { loading } = useListContext();
    return loading ? (
        <LinearProgress style={{ position: 'absolute', top: -10, left: 0, right: 0, zIndex: 1000 }} />
    ) : null;
};

const TableView1: React.FC<Props> = ({ enabledColumns, toggleColumn }) => {
    const classes = useStyles();
    const t = useTranslate();
    const dispatch = useDispatch();
    const { permissions } = useRolePermissions();

    // Selected row state from Redux to control the right-side info panel
    const selected: any = useSelector((state: any) => state?.rikutec?.ancs?.selected || {});
    const panelOpen = selected && Object.keys(selected).length > 0;
    const handleClosePanel = () => dispatch(selectAnc({}));

    const dataGridProps = {
        rowClick: (id: string, basePath: string, record: Record) => {
            dispatch(selectAnc(record));
            return null;
        },
        toFindSelected: { resource: 'ancs', key: 'selected' },
        hasBulkActions: null,
        classes: {
            checkbox: classes.checkbox,
            row: classes.list,
        },
    };

    const { simulateFingerMc, openBackofficeForms } = useAvatarAppLinks();

    return (
        <Box className='list'>
            <List
                component='div'
                {...listProps}
                pagination={<ListPagination />}
                filters={<Filters />}
                actions={<ActionList toggleColumn={toggleColumn} enabledColumns={enabledColumns} viewMode={'table'} />}
                bulkActionButtons={false}
                empty={false}
            >
                <Box
                    style={{
                        display: 'relative',
                    }}
                >
                    <LoadingBar />

                    <DataGridCustom {...dataGridProps}>
                        <RaTextField label={t('app.ancs.productTypology')} source='productTypology' />

                        <FunctionField
                            label={t('app.ancs.articleNumber')}
                            // @ts-ignore
                            render={(record: SourceBoard) => <span>{record.productCode}</span>}
                        />

                        <FunctionField
                            label={t('app.ancs.metadataAvatarType.name')}
                            // @ts-ignore
                            render={(record: SourceBoard) => (
                                <span>{getNameWithoutPrefix(record.productTypeName)}</span>
                            )}
                        />

                        <RaTextField label={t('app.ancs.serialNumber')} source='productSerialNumber' />
                        <RaTextField label={t('app.ancs.postalCode')} source='productPostalCode' />

                        <FunctionField
                            label={t('app.objects.info.created')}
                            // @ts-ignore
                            render={(record: SourceBoard) => <CreationDateField record={record} />}
                        />

                        <FunctionField
                            label={t('app.ancs.installingDate')}
                            // @ts-ignore
                            render={(record: SourceBoard) => (
                                <span>
                                    {record.productInstallationDate ? toFormatDate(record.productInstallationDate) : ''}
                                </span>
                            )}
                        />

                        <FunctionField
                            label={t('app.ancs.maintenanceLastDate')}
                            // @ts-ignore
                            render={(record: SourceBoard) => (
                                <span>
                                    {record.productLastMaintenanceDate
                                        ? toFormatDate(record.productLastMaintenanceDate)
                                        : ''}
                                </span>
                            )}
                        />

                        <FunctionField
                            label={t('app.ancs.sludgeEmptyingLastDate')}
                            // @ts-ignore
                            render={(record: SourceBoard) => (
                                <span>
                                    {record.productLastEmptyingDate ? toFormatDate(record.productLastEmptyingDate) : ''}
                                </span>
                            )}
                        />

                        <FunctionField
                            label={t('app.ancs.maintenanceThreshold')}
                            // @ts-ignore
                            render={(record: SourceBoard) => (
                                <ShowLastNoteIcon1 record={record.productLastMaintenanceData} tag={MAINTENANCE} />
                            )}
                        />

                        <FunctionField
                            label={t('app.ancs.sludgeEmptyingThreshold')}
                            // @ts-ignore
                            render={(record: SourceBoard) => (
                                <ShowLastNoteIcon1 record={record.productLastEmptyingData} tag={SLUDGE_EMPTYING} />
                            )}
                        />

                        {enabledColumns.currentState && (
                            <FunctionField
                                sortByOrder={'DESC' as any}
                                source={'productCurrentStatusName'}
                                label={t('app.ancs.nodeCurrentState')}
                                // @ts-ignore
                                render={(record: any) => record.productCurrentStatusName?.toUpperCase()}
                            />
                        )}

                        {enabledColumns.currentState && (
                            <FunctionField
                                // @ts-ignore
                                sortByOrder={'DESC'}
                                source={'productCurrentStatusDate'}
                                label={t('app.ancs.nodeDateOfTakesPlace')}
                                // @ts-ignore
                                render={(record: any) => toFormatDate(record.productCurrentStatusDate)}
                            />
                        )}

                        {enabledColumns.processingCapacity && (
                            <RaTextField
                                // @ts-ignore
                                sortByOrder={'DESC'}
                                source={'processingCapacity'}
                                label={t('app.ancs.processingCapacity')}
                            />
                        )}

                        {enabledColumns.sludgeStorageVolume && (
                            <RaTextField
                                // @ts-ignore
                                sortByOrder={'DESC'}
                                source={'sludgeStorageVolume'}
                                label={t('app.ancs.sludgeStorageVolume')}
                            />
                        )}

                        {enabledColumns.totalUsefulVolume && (
                            <RaTextField
                                // @ts-ignore
                                sortByOrder={'DESC'}
                                source={'totalUsefulVolume'}
                                label={t('app.ancs.totalUsefulVolume')}
                            />
                        )}

                        {enabledColumns.model && (
                            <RaTextField // @ts-ignore
                                sortByOrder={'DESC'}
                                source={'model'}
                                label={t('app.ancs.model')}
                            />
                        )}

                        <FunctionField
                            label={t('app.ancs.accessToFinger')}
                            // @ts-ignore
                            render={(record: SourceBoard) => (
                                <IconButton onClick={() => simulateFingerMc(record.avatarId)}>
                                    <Tooltip title={t('app.ancs.accessToFinger')}>
                                        <PhoneAndroidIcon />
                                    </Tooltip>
                                </IconButton>
                            )}
                        />

                        {hasRoles(['ROLE_RKT_PROCESSUS_SPECIFIQUE_WEBAPP_DASHBORD'], permissions || []) && (
                            <FunctionField
                                label={t('app.ancs.vueSpecifique')}
                                // @ts-ignore
                                render={(record: SourceBoard) => (
                                    <IconButton onClick={() => openBackofficeForms(record.avatarId)}>
                                        <Tooltip title={t('app.ancs.vueSpecifique')}>
                                            <AssignmentIcon />
                                        </Tooltip>
                                    </IconButton>
                                )}
                            />
                        )}
                    </DataGridCustom>

                    {panelOpen && <RightInfoDrawer open={panelOpen} onClose={handleClosePanel} selected={selected} />}
                </Box>
            </List>
        </Box>
    );
};

export default TableView1;
